import { supabase }   from '../supabase.js';
import { pageQuery }  from '../lib/egress.js';
import { openDrillPanel } from '../components/drill-panel.js';

/* ============================================================
   HR · Employees — Londmarx's own salaried staff (admin only).
   Kept apart from learners. Pay is set here as TAKE-HOME; the
   payslip engine grosses it up (see hr-payslips.js).
============================================================ */

const esc = s => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
const rands = n => 'R ' + Number(n || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};
const today = () => new Date().toISOString().slice(0, 10);

/* universal branch codes — auto-fill when a bank is picked */
const BANKS = {
  'ABSA': '632005', 'African Bank': '430000', 'Capitec': '470010',
  'Discovery Bank': '679000', 'FNB': '250655', 'Investec': '580105',
  'Nedbank': '198765', 'Standard Bank': '051001', 'TymeBank': '678910',
};

const EMP_COLS = 'id,employee_number,first_name,surname,national_id,date_of_birth,gender,occupation,' +
  'contracted_net_salary,contract_type,start_date,end_date,uif_contributor,tax_status,directive_pct,' +
  'payment_method,bank_name,bank_account_number,bank_account_type,branch_code,address,email,phone,is_active';

let _state = { search: '', filter: 'active' };
let _pendingOpen = null;

/** Called from HR · Payslips to jump here with an employee's panel open. */
export function openEmployeeFromElsewhere(id) {
  _pendingOpen = id;
  location.hash = '#/hr-employees';
}

function contractInfo(e) {
  if (e.contract_type === 'continuous' || !e.end_date) return { label: 'Continuous', cls: 'ok' };
  if (e.end_date < today()) return { label: `Ended ${fmtDate(e.end_date)}`, cls: 'ended' };
  return { label: `Fixed term to ${fmtDate(e.end_date)}`, cls: 'ok' };
}

function missingInfo(e) {
  return [
    !e.national_id         && 'ID number',
    !e.bank_account_number && 'bank details',
    !e.occupation          && 'occupation',
  ].filter(Boolean);
}

/* ── Main render ────────────────────────────────────────────── */
export async function renderHrEmployees() {
  const el = document.getElementById('view-hr-employees');

  if (!window.__access?.hr) {
    el.innerHTML = `<div class="page-header"><h1>Employees</h1><p>Staff HR is not switched on for you.</p></div>`;
    return;
  }
  const state = _state;

  el.innerHTML = `
<style>
  .hr-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; font-size:13px; font-weight:500;
    border:1px solid var(--border); border-radius:8px; background:var(--surface); color:var(--text);
    cursor:pointer; white-space:nowrap; }
  .hr-btn:hover:not(:disabled) { background:var(--surface-2); }
  .hr-btn:disabled { opacity:.5; cursor:default; }
  .hr-btn.primary { background:var(--orange); border-color:var(--orange); color:#fff; }
  .hr-btn.primary:hover:not(:disabled) { background:var(--orange-dark); }
  .hr-kpis { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:12px; margin-bottom:16px; }
  .hr-kpi { padding:14px 16px; background:var(--surface); border:1px solid var(--border); border-radius:10px; }
  .hr-kpi .lbl { font-size:.72rem; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.03em; }
  .hr-kpi .val { font-size:1.25rem; font-weight:700; margin-top:4px; font-variant-numeric:tabular-nums; }
  .hr-badge { display:inline-block; padding:2px 9px; border-radius:12px; font-size:.72rem; font-weight:600; white-space:nowrap; }
  .hr-badge.ok     { background:#d1fae5; color:#065f46; }
  .hr-badge.ended  { background:#f1f5f9; color:#475569; }
  .hr-badge.warn   { background:#fef3c7; color:#92400e; }
  .hr-badge.off    { background:#fee2e2; color:#991b1b; }
  .hr-toolbar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
  .hr-toolbar input, .hr-toolbar select { padding:6px 10px; border:1px solid var(--border); border-radius:6px;
    background:var(--surface); color:var(--text); font-size:.82rem; }
  .hr-toolbar input { flex:1; min-width:180px; max-width:320px; }
  .hr-table-wrap { overflow-x:auto; }
  #hr-tbody td { vertical-align:middle; }
  .hr-num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
  .hr-form { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .hr-form label { display:flex; flex-direction:column; gap:3px; font-size:.74rem; font-weight:600; color:var(--text-muted); }
  .hr-form input, .hr-form select, .hr-form textarea { padding:7px 9px; border:1px solid var(--border); border-radius:6px;
    background:var(--surface); color:var(--text); font-size:.85rem; font-family:inherit; }
  .hr-form .full { grid-column:1 / -1; }
  .hr-form .check { flex-direction:row; align-items:center; gap:6px; }
  .hr-form h4 { grid-column:1 / -1; margin:8px 0 0; font-size:.78rem; text-transform:uppercase;
    letter-spacing:.04em; color:var(--text); }
  .hr-msg { font-size:.8rem; }
  @media (max-width:560px) { .hr-form { grid-template-columns:1fr; } }
</style>

<div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px">
  <div>
    <h1>Employees</h1>
    <p>Londmarx staff, kept separate from learners. Salaries are take-home amounts.</p>
  </div>
  <button class="hr-btn primary" id="hr-add">+ Add employee</button>
</div>

<div class="hr-kpis" id="hr-kpis"></div>

<div class="hr-toolbar">
  <input type="search" id="hr-search" placeholder="Search name, employee no. or ID" value="${esc(state.search)}">
  <select id="hr-filter">
    <option value="active">Active</option>
    <option value="inactive">Former</option>
    <option value="">All</option>
  </select>
</div>

<div class="table-card hr-table-wrap">
  <table class="data-table">
    <thead><tr>
      <th>Emp No</th><th>Name</th><th>ID Number</th><th>Occupation</th>
      <th>Contract</th><th class="hr-num">Take-home</th><th>Bank</th><th></th>
    </tr></thead>
    <tbody id="hr-tbody"><tr><td colspan="8" class="no-data">Loading…</td></tr></tbody>
  </table>
</div>`;

  document.getElementById('hr-filter').value = state.filter;

  let employees = [];

  async function reload() {
    const { data } = await pageQuery('employees', { select: EMP_COLS, order: 'employee_number' });
    employees = data || [];
    renderKpis();
    renderRows();
  }

  function renderKpis() {
    const active  = employees.filter(e => e.is_active);
    const current = active.filter(e => contractInfo(e).cls === 'ok');
    const gaps    = active.filter(e => missingInfo(e).length);
    const kpis = [
      ['Active employees',      active.length],
      ['In contract today',     current.length],
      ['Monthly take-home',     rands(current.reduce((t, e) => t + Number(e.contracted_net_salary), 0))],
      ['Missing details',       gaps.length],
    ];
    document.getElementById('hr-kpis').innerHTML = kpis.map(([l, v]) =>
      `<div class="hr-kpi"><div class="lbl">${l}</div><div class="val">${v}</div></div>`).join('');
  }

  function visible() {
    const q = state.search.trim().toLowerCase();
    return employees.filter(e =>
      (state.filter === '' || (state.filter === 'active') === e.is_active) &&
      (!q || `${e.employee_number} ${e.first_name} ${e.surname} ${e.national_id || ''}`.toLowerCase().includes(q)));
  }

  function renderRows() {
    const rows  = visible();
    const tbody = document.getElementById('hr-tbody');
    if (!rows.length) {
      tbody.innerHTML = `<tr><td colspan="8" class="no-data">No employees match.</td></tr>`;
      return;
    }
    tbody.innerHTML = rows.map(e => {
      const c    = contractInfo(e);
      const miss = missingInfo(e);
      return `
      <tr class="clickable-row" data-id="${esc(e.id)}">
        <td>${esc(e.employee_number)}</td>
        <td><strong>${esc(e.first_name)} ${esc(e.surname)}</strong></td>
        <td>${e.national_id ? esc(e.national_id) : '<span class="hr-badge warn">Outstanding</span>'}</td>
        <td>${esc(e.occupation || '—')}</td>
        <td>${e.is_active ? `<span class="hr-badge ${c.cls}">${esc(c.label)}</span>` : '<span class="hr-badge off">Former</span>'}</td>
        <td class="hr-num">${rands(e.contracted_net_salary)}</td>
        <td>${e.bank_name ? esc(e.bank_name) : '<span class="hr-badge warn">None</span>'}</td>
        <td>${miss.length ? `<span class="hr-badge warn" title="Missing: ${esc(miss.join(', '))}">${miss.length} missing</span>` : ''}</td>
      </tr>`;
    }).join('');

    tbody.querySelectorAll('tr.clickable-row').forEach(tr =>
      tr.addEventListener('click', () => openEmployee(employees.find(e => e.id === tr.dataset.id))));
  }

  /* ── Add / edit panel ───────────────────────────────────── */
  function openEmployee(e) {
    const isNew = !e;
    e = e || { contract_type: 'fixed_term', start_date: today(), uif_contributor: true,
               tax_status: 'Normal', payment_method: 'EFT', is_active: true };
    const opt = (list, cur) => list.map(([v, l]) =>
      `<option value="${esc(v)}" ${String(cur ?? '') === String(v) ? 'selected' : ''}>${esc(l)}</option>`).join('');

    const html = `
      <form id="hr-emp-form" class="hr-form">
        <h4>Personal</h4>
        <label>First name(s) <input name="first_name" required value="${esc(e.first_name)}"></label>
        <label>Surname <input name="surname" required value="${esc(e.surname)}"></label>
        <label>ID number <input name="national_id" maxlength="13" inputmode="numeric" value="${esc(e.national_id)}"></label>
        <label>Occupation <input name="occupation" value="${esc(e.occupation)}"></label>
        <label>Email <input name="email" type="email" value="${esc(e.email)}"></label>
        <label>Phone <input name="phone" value="${esc(e.phone)}"></label>
        <label class="full">Address <textarea name="address" rows="2">${esc(e.address)}</textarea></label>

        <h4>Contract &amp; pay</h4>
        <label>Take-home salary (R)
          <input name="contracted_net_salary" type="number" min="0.01" step="0.01" required value="${esc(e.contracted_net_salary)}">
        </label>
        <label>Contract type
          <select name="contract_type">${opt([['fixed_term','Fixed term'],['continuous','Continuous']], e.contract_type)}</select>
        </label>
        <label>Start date <input name="start_date" type="date" required value="${esc(e.start_date)}"></label>
        <label>End date <input name="end_date" type="date" value="${esc(e.end_date)}"></label>
        <label>Tax status
          <select name="tax_status">${opt([['Normal','Normal (SARS tables)'],['Directive','Tax directive'],['Exempt','Exempt']], e.tax_status)}</select>
        </label>
        <label>Directive % <input name="directive_pct" type="number" min="0" max="100" step="0.01" value="${esc(e.directive_pct)}"></label>
        <label class="check full"><input name="uif_contributor" type="checkbox" ${e.uif_contributor ? 'checked' : ''}> UIF contributor</label>

        <h4>Banking</h4>
        <label>Bank
          <input name="bank_name" list="hr-banks" value="${esc(e.bank_name)}">
          <datalist id="hr-banks">${Object.keys(BANKS).map(b => `<option value="${esc(b)}">`).join('')}</datalist>
        </label>
        <label>Branch code <input name="branch_code" value="${esc(e.branch_code)}"></label>
        <label>Account number <input name="bank_account_number" inputmode="numeric" value="${esc(e.bank_account_number)}"></label>
        <label>Account type
          <select name="bank_account_type">${opt([['',''],['Savings','Savings'],['Cheque','Cheque / Current'],['Transmission','Transmission']], e.bank_account_type)}</select>
        </label>
        <label>Payment method
          <select name="payment_method">${opt([['EFT','EFT'],['Cash','Cash'],['Cheque','Cheque']], e.payment_method)}</select>
        </label>
        ${isNew ? '' : `<label class="check"><input name="is_active" type="checkbox" ${e.is_active ? 'checked' : ''}> Active employee</label>`}

        <div class="full" style="display:flex;gap:8px;align-items:center;margin-top:6px">
          <button class="hr-btn primary" type="submit">${isNew ? 'Add employee' : 'Save changes'}</button>
          <span class="hr-msg" id="hr-emp-msg"></span>
        </div>
        ${isNew ? '' : `<p class="full" style="font-size:.76rem;color:var(--text-muted);margin:0">
          Pay or tax changes recalculate this employee's <strong>draft</strong> payslips automatically.
          Final and paid payslips are left as they are.</p>`}
      </form>`;

    openDrillPanel({
      eyebrow: isNew ? 'HR · New employee' : `HR · ${e.employee_number}`,
      title:   isNew ? 'Add employee' : `${e.first_name} ${e.surname}`,
      html,
      onMount: body => wireForm(body, e, isNew),
    });
  }

  function wireForm(body, e, isNew) {
    const form = body.querySelector('#hr-emp-form');
    const msg  = body.querySelector('#hr-emp-msg');
    const fail = t => { msg.textContent = t; msg.style.color = '#b91c1c'; };

    form.bank_name.addEventListener('change', () => {
      const code = BANKS[form.bank_name.value.trim()];
      if (code) form.branch_code.value = code;
    });
    const syncEnd = () => {
      const cont = form.contract_type.value === 'continuous';
      form.end_date.disabled = cont;
      if (cont) form.end_date.value = '';
    };
    form.contract_type.addEventListener('change', syncEnd);
    syncEnd();

    form.addEventListener('submit', async ev => {
      ev.preventDefault();
      const fd  = new FormData(form);
      const val = k => (String(fd.get(k) ?? '').trim() || null);

      const nid = val('national_id');
      if (nid && !/^\d{13}$/.test(nid)) return fail('ID number must be 13 digits.');
      const end = val('end_date');
      if (end && end < val('start_date')) return fail('End date is before start date.');
      const tax = fd.get('tax_status');
      if (tax === 'Directive' && !val('directive_pct')) return fail('Enter the directive percentage.');

      const row = {
        first_name:            val('first_name'),
        surname:               val('surname'),
        national_id:           nid,
        occupation:            val('occupation'),
        email:                 val('email'),
        phone:                 val('phone'),
        address:               val('address'),
        contracted_net_salary: Number(fd.get('contracted_net_salary')),
        contract_type:         fd.get('contract_type'),
        start_date:            val('start_date'),
        end_date:              fd.get('contract_type') === 'continuous' ? null : end,
        tax_status:            tax,
        directive_pct:         tax === 'Directive' ? Number(fd.get('directive_pct')) : null,
        uif_contributor:       fd.get('uif_contributor') === 'on',
        bank_name:             val('bank_name'),
        branch_code:           val('branch_code'),
        bank_account_number:   val('bank_account_number'),
        bank_account_type:     val('bank_account_type'),
        payment_method:        fd.get('payment_method'),
      };
      if (!isNew) row.is_active = fd.get('is_active') === 'on';

      const btn = form.querySelector('button[type=submit]');
      btn.disabled = true;
      const q = isNew
        ? supabase.from('employees').insert(row).select('id').single()   // employee_number assigned by trigger
        : supabase.from('employees').update(row).eq('id', e.id).select('id').single();
      const { data, error } = await q;
      btn.disabled = false;
      if (error) {
        return fail(error.code === '23505' ? 'Another employee already has that ID number.' : error.message);
      }
      await reload();
      openEmployee(employees.find(x => x.id === data.id));
      const m = document.getElementById('hr-emp-msg');
      if (m) { m.textContent = isNew ? 'Employee added.' : 'Saved.'; m.style.color = '#065f46'; }
    });
  }

  /* ── Toolbar ─────────────────────────────────────────────── */
  document.getElementById('hr-search').addEventListener('input', ev => { state.search = ev.target.value; renderRows(); });
  document.getElementById('hr-filter').addEventListener('change', ev => { state.filter = ev.target.value; renderRows(); });
  document.getElementById('hr-add').addEventListener('click', () => openEmployee(null));

  await reload();

  if (_pendingOpen) {
    const target = employees.find(e => e.id === _pendingOpen);
    _pendingOpen = null;
    if (target) openEmployee(target);
  }
}
